import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useRef } from 'react'
import { AuthContext } from 'src/contexts/Auth'
import { QueryKeys } from 'src/constants/QueryKeys'
import {
    AffiliateCode,
    AffiliateMe,
    ApplyBody,
    Commission,
    CommissionStatus,
    InvitePreview,
    Paginated,
    Payout,
    PublicProgram,
    Referral,
    ReferralStatus,
    Stats,
    StatsRange,
} from 'src/types/Affiliate'
import { apiDelete, apiGet, apiPatch, apiPost } from 'src/utils/affiliateApi'
import { getReason, retryServerErrors } from 'src/utils/apiReason'

const ACTIVE_OR_PAUSED = ['active', 'paused']
export const isActiveOrPaused = (me?: AffiliateMe | null) => !!me && ACTIVE_OR_PAUSED.includes(me.status)

/* --------------------------------- Queries --------------------------------- */

/**
 * The caller's affiliate record, or null when they're not in the program (403 not_affiliate).
 * Also keeps /users/@me's `affiliate` summary (which drives the nav) in step with it, so applying, accepting an
 * invite or an admin status change shows up everywhere without a reload.
 */
export const useAffiliateMe = (enabled = true) => {
    const { userInfo, refreshUser } = useContext(AuthContext)
    const query = useQuery({
        queryKey: [QueryKeys.affiliateMe],
        queryFn: async () => {
            try {
                return await apiGet<AffiliateMe>('/@me')
            } catch (error) {
                if (getReason(error) === 'not_affiliate') return null
                throw error
            }
        },
        enabled,
        retry: retryServerErrors,
    })

    const me = query.data
    const known = userInfo?.affiliate || null
    // `code` is only returned while active or paused, so compare it only when present
    const stale =
        me !== undefined &&
        !!userInfo?.id &&
        (me ? !known || known.status !== me.status || (!!me.code && known.code !== me.code) : !!known)
    const syncedFor = useRef<string | null>(null)
    useEffect(() => {
        const signature = me === undefined ? null : me ? `${me.status}:${me.code || ''}` : 'none'
        // One refresh per distinct state, so a backend that disagrees can't cause a refetch loop
        if (stale && syncedFor.current !== signature) {
            syncedFor.current = signature
            refreshUser()
        }
    }, [stale, me, refreshUser])

    return query
}

export const usePublicProgram = () =>
    useQuery({
        queryKey: [QueryKeys.affiliateProgramPublic],
        queryFn: () => apiGet<PublicProgram>('/program/public'),
        retry: retryServerErrors,
        staleTime: 5 * 60 * 1000,
    })

export const useAffiliateStats = (range: StatsRange, enabled: boolean) =>
    useQuery({
        queryKey: [QueryKeys.affiliateStats, range],
        queryFn: () => apiGet<Stats>('/@me/stats', { range }),
        enabled,
        retry: retryServerErrors,
        placeholderData: keepPreviousData,
    })

/** All of the caller's codes: primary first, then campaign, then retired. */
export const useAffiliateCodes = (enabled: boolean) =>
    useQuery({
        queryKey: [QueryKeys.affiliateCodes],
        queryFn: () => apiGet<AffiliateCode[]>('/@me/codes'),
        enabled,
        retry: retryServerErrors,
    })

export const useAffiliateReferrals = (page: number, status: ReferralStatus | '', code: string, enabled: boolean) =>
    useQuery({
        queryKey: [QueryKeys.affiliateReferrals, page, status, code],
        queryFn: () =>
            apiGet<Paginated<Referral>>('/@me/referrals', {
                page,
                limit: 20,
                status: status || undefined,
                code: code || undefined,
            }),
        enabled,
        retry: retryServerErrors,
        placeholderData: keepPreviousData,
    })

export const useAffiliateCommissions = (page: number, status: CommissionStatus | '', enabled: boolean) =>
    useQuery({
        queryKey: [QueryKeys.affiliateCommissions, page, status],
        queryFn: () =>
            apiGet<Paginated<Commission>>('/@me/commissions', { page, limit: 20, status: status || undefined }),
        enabled,
        retry: retryServerErrors,
        placeholderData: keepPreviousData,
    })

export const useAffiliatePayouts = (page: number, enabled: boolean) => {
    const queryClient = useQueryClient()
    const query = useQuery({
        queryKey: [QueryKeys.affiliatePayouts, page],
        queryFn: () => apiGet<Paginated<Payout>>('/@me/payouts', { page, limit: 20 }),
        enabled,
        retry: retryServerErrors,
        placeholderData: keepPreviousData,
        // A payout in `processing` settles asynchronously; keep checking until it does.
        refetchInterval: (query) =>
            query.state.data?.results.some((payout) => payout.status === 'processing') ? 5000 : false,
    })

    // When a processing payout settles, the balances on the tiles change too.
    const processing = !!query.data?.results.some((payout) => payout.status === 'processing')
    const wasProcessing = useRef(processing)
    useEffect(() => {
        if (wasProcessing.current && !processing) {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateMe] })
            queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateStats] })
        }
        wasProcessing.current = processing
    }, [processing, queryClient])

    return query
}

export const useInvitePreview = (token: string) =>
    useQuery({
        queryKey: [QueryKeys.affiliateInvite, token],
        queryFn: () => apiGet<InvitePreview>(`/invites/${token}`),
        retry: retryServerErrors,
    })

/* -------------------------------- Mutations -------------------------------- */

/** Refresh everything on the Affiliate tab after something that changes balances or status. */
const useInvalidateAffiliate = () => {
    const queryClient = useQueryClient()
    return () =>
        Promise.all(
            [
                QueryKeys.affiliateMe,
                QueryKeys.affiliateStats,
                QueryKeys.affiliateCommissions,
                QueryKeys.affiliatePayouts,
            ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
        )
}

const useSetMe = () => {
    const queryClient = useQueryClient()
    return (me: AffiliateMe) => queryClient.setQueryData([QueryKeys.affiliateMe], me)
}

export const useApply = () => {
    const setMe = useSetMe()
    return useMutation({
        mutationFn: (body: ApplyBody) => apiPost<AffiliateMe>('/apply', body),
        onSuccess: setMe,
    })
}

export const useAcceptInvite = (token: string) => {
    const setMe = useSetMe()
    return useMutation({
        mutationFn: (termsVersion: string) =>
            apiPost<AffiliateMe>(`/invites/${token}/accept`, { acceptTerms: true, termsVersion }),
        onSuccess: setMe,
    })
}

export const useAcceptTerms = () => {
    const setMe = useSetMe()
    return useMutation({
        mutationFn: (termsVersion: string) => apiPost<AffiliateMe>('/@me/terms', { termsVersion }),
        onSuccess: setMe,
    })
}

/** After any code change: the codes list and the per-code results in stats. */
const useRefreshCodes = () => {
    const queryClient = useQueryClient()
    return (codes?: AffiliateCode[]) => {
        if (codes) queryClient.setQueryData([QueryKeys.affiliateCodes], codes)
        else queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateCodes] })
        queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateStats] })
    }
}

/** Change the main code. The old one is retired and keeps crediting the affiliate. */
export const useChangeMainCode = () => {
    const setMe = useSetMe()
    const refreshCodes = useRefreshCodes()
    return useMutation({
        mutationFn: (code: string) => apiPatch<AffiliateMe>('/@me', { code }),
        onSuccess: (me) => {
            setMe(me)
            refreshCodes()
        },
    })
}

export const useAddCode = () => {
    const refreshCodes = useRefreshCodes()
    return useMutation({
        mutationFn: (body: { code: string; label?: string; trial: boolean }) =>
            apiPost<AffiliateCode[]>('/@me/codes', body),
        onSuccess: refreshCodes,
    })
}

export const useEditCode = () => {
    const refreshCodes = useRefreshCodes()
    return useMutation({
        mutationFn: ({ code, ...body }: { code: string; label?: string; trial?: boolean }) =>
            apiPatch<AffiliateCode[]>(`/@me/codes/${encodeURIComponent(code)}`, body),
        onSuccess: refreshCodes,
    })
}

/** Retire a campaign code: it leaves the active list, but links using it keep crediting the affiliate. */
export const useRemoveCode = () => {
    const refreshCodes = useRefreshCodes()
    return useMutation({
        mutationFn: (code: string) => apiDelete<AffiliateCode[]>(`/@me/codes/${encodeURIComponent(code)}`),
        onSuccess: refreshCodes,
    })
}

export const useRequestPayout = () => {
    const invalidate = useInvalidateAffiliate()
    return useMutation({
        mutationFn: () => apiPost<Payout>('/@me/payouts'),
        onSettled: invalidate,
    })
}

export const useConnectOnboard = () =>
    useMutation({
        mutationFn: (country: string) => apiPost<{ url: string }>('/@me/connect/onboard', { country }),
        onSuccess: ({ url }) => {
            window.location.href = url
        },
    })

export const useConnectDashboard = () =>
    useMutation({
        mutationFn: () => apiPost<{ url: string }>('/@me/connect/dashboard'),
    })
